import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CodeTab } from "./CodeTab";
import { PreviewTab } from "./PreviewTab";
import { PageHtmlChatPanel } from "./PageHtmlChatPanel";
import { AnalyticsTab } from "./AnalyticsTab";
import { ResponsesTab } from "./ResponsesTab";
import { OrdersTab } from "./OrdersTab";
import { SettingsTab } from "./SettingsTab";
import { AccessRequestsTab, type PageAccessSettingsState } from "./AccessRequestsTab";
import { TemplatesTab, type AppliedLinkInBioPage } from "./TemplatesTab";
import type { PageOnSubmitActionType } from "@/types/pageFormSubmit";

const TAB_IDS = [
  "preview",
  "code",
  "templates",
  "analytics",
  "responses",
  "orders",
  "settings",
  "access",
] as const;
type TabId = (typeof TAB_IDS)[number];

function isValidTab(tab: string | null, pageId?: string): tab is TabId {
  if (!tab || !TAB_IDS.includes(tab as TabId)) return false;
  if (
    (tab === "responses" ||
      tab === "orders" ||
      tab === "access" ||
      tab === "templates") &&
    !pageId
  ) {
    return false;
  }
  return true;
}

interface PageHtmlEditorProps {
  pageId?: string;
  html: string;
  onHtmlChange: (html: string) => void;
  title: string;
  route: string;
  onTitleChange: (title: string) => void;
  onRouteChange: (route: string) => void;
  pageType?: "html" | "markdown" | "redirect";
  onPageTypeChange?: (type: "html" | "markdown" | "redirect") => void;
  redirectUrl?: string;
  onRedirectUrlChange?: (url: string) => void;
  formOnSubmit?: Record<string, PageOnSubmitActionType[]>;
  onFormOnSubmitChange?: (formOnSubmit: Record<string, PageOnSubmitActionType[]>) => void;
  access: PageAccessSettingsState;
  onAccessChange: (access: PageAccessSettingsState) => void;
  onSave?: (html?: string) => void;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
  onTemplateApplied?: (page: AppliedLinkInBioPage) => void;
}

export function PageHtmlEditor({
  pageId,
  html,
  onHtmlChange,
  title,
  route,
  onTitleChange,
  onRouteChange,
  pageType = "html",
  onPageTypeChange,
  redirectUrl,
  onRedirectUrlChange,
  formOnSubmit,
  onFormOnSubmitChange,
  access,
  onAccessChange,
  onSave,
  isSaving,
  hasUnsavedChanges,
  onTemplateApplied,
}: PageHtmlEditorProps) {
  const [previewRefreshTrigger, setPreviewRefreshTrigger] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const isRedirectPage = pageType === "redirect";
  const defaultTab: TabId = isRedirectPage ? "settings" : "preview";
  const activeTab: TabId = isValidTab(tabFromUrl, pageId) ? (tabFromUrl as TabId) : defaultTab;
  const effectiveTab: TabId =
    isRedirectPage &&
    (activeTab === "preview" ||
      activeTab === "code" ||
      activeTab === "templates" ||
      activeTab === "responses" ||
      activeTab === "orders" ||
      activeTab === "access")
      ? "settings"
      : activeTab;

  const setActiveTab = (tab: TabId) => {
    if (isRedirectPage && (tab === "preview" || tab === "code" || tab === "templates" || tab === "access")) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true }
    );
  };

  const showChatPanel = !isRedirectPage && (effectiveTab === "preview" || effectiveTab === "code");

  return (
    <div className="flex-1 overflow-hidden flex flex-col min-h-0 min-w-0">
      <div className="flex border-b border-gray-200 shrink-0 overflow-x-auto min-w-0">
        {!isRedirectPage && (
          <button
            type="button"
            onClick={() => setActiveTab("preview")}
            className={`shrink-0 px-4 py-2 text-sm font-medium ${
              activeTab === "preview"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Preview
          </button>
        )}
        {!isRedirectPage && (
          <button
            type="button"
            onClick={() => setActiveTab("code")}
            className={`shrink-0 px-4 py-2 text-sm font-medium ${
              activeTab === "code"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Code
          </button>
        )}
        {pageId && !isRedirectPage && (
          <button
            type="button"
            onClick={() => setActiveTab("templates")}
            className={`shrink-0 px-4 py-2 text-sm font-medium ${
              effectiveTab === "templates"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Templates
          </button>
        )}
        <button
          type="button"
          onClick={() => setActiveTab("analytics")}
          className={`shrink-0 px-4 py-2 text-sm font-medium ${
            effectiveTab === "analytics"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Analytics
        </button>
        {pageId && !isRedirectPage && (
          <button
            type="button"
            onClick={() => setActiveTab("responses")}
            className={`shrink-0 px-4 py-2 text-sm font-medium ${
              effectiveTab === "responses"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Responses
          </button>
        )}
        {pageId && !isRedirectPage && (
          <button
            type="button"
            onClick={() => setActiveTab("orders")}
            className={`shrink-0 px-4 py-2 text-sm font-medium ${
              effectiveTab === "orders"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Orders
          </button>
        )}
        <button
          type="button"
          onClick={() => setActiveTab("settings")}
          className={`shrink-0 px-4 py-2 text-sm font-medium ${
            effectiveTab === "settings"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Settings
        </button>
        {pageId && !isRedirectPage && (
          <button
            type="button"
            onClick={() => setActiveTab("access")}
            className={`shrink-0 px-4 py-2 text-sm font-medium ${
              effectiveTab === "access"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Access
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden flex min-w-0">
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col min-w-0">
          {effectiveTab === "preview" && (
            <PreviewTab
              onSave={onSave}
              isSaving={isSaving}
              hasUnsavedChanges={hasUnsavedChanges}
              route={route}
              refreshTrigger={previewRefreshTrigger}
            />
          )}
          {effectiveTab === "code" && (
            <CodeTab
              html={html}
              onHtmlChange={onHtmlChange}
              onSave={onSave}
              isSaving={isSaving}
              hasUnsavedChanges={hasUnsavedChanges}
              route={route}
            />
          )}
          {effectiveTab === "templates" && (
            <TemplatesTab pageId={pageId} onTemplateApplied={onTemplateApplied} />
          )}
          {effectiveTab === "analytics" && <AnalyticsTab pageId={pageId} />}
          {effectiveTab === "responses" && <ResponsesTab pageId={pageId} />}
          {effectiveTab === "orders" && <OrdersTab pageId={pageId} />}
          {effectiveTab === "settings" && (
            <SettingsTab
              title={title}
              route={route}
              onTitleChange={onTitleChange}
              onRouteChange={onRouteChange}
              pageType={pageType}
              onPageTypeChange={onPageTypeChange}
              redirectUrl={redirectUrl}
              onRedirectUrlChange={onRedirectUrlChange}
              formOnSubmit={formOnSubmit}
              onFormOnSubmitChange={onFormOnSubmitChange}
              onSave={onSave}
              isSaving={isSaving}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          )}
          {effectiveTab === "access" && (
            <AccessRequestsTab
              pageId={pageId}
              access={access}
              onAccessChange={onAccessChange}
              onSave={onSave}
              isSaving={isSaving}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          )}
        </div>
        {showChatPanel && (
          <PageHtmlChatPanel
            html={html}
            onHtmlChange={onHtmlChange}
            onSave={onSave}
            onGenerationComplete={() => setPreviewRefreshTrigger((trigger) => trigger + 1)}
          />
        )}
      </div>
    </div>
  );
}
